/**
 * Calcolo del preventivo analitico di un'offerta.
 *
 * È il foglio "Riesame Offerta" portato nel gestionale: voci × quantità ×
 * tariffa, più le fee, più la maggiorazione. Stessa identica logica in
 * server/lib/preventivo.js — il server la usa per il documento, qui serve per
 * vedere i numeri mentre si compila. Se cambia una, va cambiata l'altra.
 *
 * Verso il cliente fee e maggiorazione NON compaiono come voci: si ripartiscono
 * sui prezzi unitari, in proporzione. Il totale della tabella al cliente è
 * quindi l'importo dell'offerta, al centesimo.
 */

export const TIPI_TARIFFA = [
  { v: 'giorno', l: 'Giornata', giornate: 1 },
  { v: 'mezza', l: 'Mezza giornata', giornate: 0.5 },
  { v: 'back', l: 'Back office', giornate: 1 },
  { v: 'libera', l: 'Prezzo libero', giornate: 0 },
];

// Le voci del foglio di riesame: si parte da queste, poi si aggiungono o tolgono.
export const VOCI_PREDEFINITE = [
  { descrizione: 'Interventi in azienda (mezza giornata)', tariffa: 'mezza' },
  { descrizione: 'Interventi in azienda (giornata)', tariffa: 'giorno' },
  { descrizione: 'Giornate di back office', tariffa: 'back' },
  { descrizione: 'Audit', tariffa: 'giorno' },
  { descrizione: 'Riesame', tariffa: 'mezza' },
  { descrizione: 'Assistenza audit', tariffa: 'giorno' },
];

export const preventivoVuoto = () => ({
  attivo: true,
  mostraDettaglio: false,
  tariffe: { giorno: '', mezza: '', back: '' },
  consulenti: [null, null, null],          // id consulente per colonna
  righe: VOCI_PREDEFINITE.map(v => ({ ...v, prezzo: '', quantita: ['', '', ''] })),
  fee: [{ descrizione: 'Fee segnalazione', importo: '' },
        { descrizione: 'Fee supporto tecnico', importo: '' }],
  // Nessuna maggiorazione di partenza: il 5% predefinito finiva nei prezzi
  // unitari del documento (550 €/giornata uscivano 577,50) senza che nessuno
  // l'avesse scelto. Chi la vuole la scrive, e il pannello mostra l'effetto.
  maggiorazione: 0,
});

const n = (v) => Number(String(v ?? '').replace(',', '.')) || 0;
const r2 = (v) => Math.round(v * 100) / 100;

/**
 * Macro voce: un titolo che raggruppa le attività di dettaglio che lo seguono,
 * fino al titolo successivo. È il modo in cui i preventivi si leggono davvero
 * — "AI: 3 gg" e sotto le quattro attività che ci stanno dentro — e senza non
 * si riusciva a riportarli nel gestionale.
 *
 * Non ha quantità né tariffa: il suo importo è la somma dei figli.
 */
export const isSezione = (riga) => !!riga?.sezione;

/** Le righe di dettaglio che stanno sotto la macro voce in posizione `i`. */
export function righeDellaSezione(righe, i) {
  const dentro = [];
  for (let j = i + 1; j < righe.length && !isSezione(righe[j]); j++) dentro.push(righe[j]);
  return dentro;
}

export const prezzoUnitario = (prev, riga) =>
  isSezione(riga) ? 0 : (riga.tariffa === 'libera' ? n(riga.prezzo) : n(prev.tariffe?.[riga.tariffa]));
export const quantitaRiga = (riga) =>
  isSezione(riga) ? 0 : (riga.quantita || []).reduce((s, q) => s + n(q), 0);

export function calcolaPreventivo(prev) {
  if (!prev) return null;
  const righe = (prev.righe || []).map(r => {
    const q = quantitaRiga(r);
    const pu = prezzoUnitario(prev, r);
    return { ...r, q, pu, importo: r2(q * pu) };
  });
  const subtotale = r2(righe.reduce((s, r) => s + r.importo, 0));
  const totaleFee = r2((prev.fee || []).reduce((s, f) => s + n(f.importo), 0));
  // Prezzo pieno: attività, fee e maggiorazione
  const offerta = r2((subtotale + totaleFee) * (1 + n(prev.maggiorazione) / 100));

  // Sconto facoltativo, in percentuale o in euro sul prezzo pieno
  const sc = prev.sconto || {};
  const sconto = sc.tipo === 'percentuale' ? r2(offerta * n(sc.valore) / 100) : r2(n(sc.valore));
  const calcolato = r2(offerta - sconto);
  // Prezzo finale: quello arrotondato a mano, se indicato
  const manuale = String(prev.prezzoFinale ?? '').trim() !== '';
  const prezzo = manuale ? r2(n(prev.prezzoFinale)) : calcolato;

  // Tabella al cliente. Con uno sconto le voci restano a prezzo pieno e la
  // riga di sconto porta al prezzo finale: l'arrotondamento finisce lì, dove
  // il cliente se lo aspetta. Senza sconto le voci si scalano fino al prezzo.
  const conSconto = sconto > 0 && prezzo < offerta;
  const obiettivo = conSconto ? offerta : prezzo;
  const fattore = subtotale > 0 ? obiettivo / subtotale : 0;
  // Le macro voci restano nella tabella del cliente, ma solo se sotto hanno
  // qualcosa: un titolo seguito dal nulla è rumore.
  const visibili = righe
    .filter((r, i) => (isSezione(r) ? righeDellaSezione(righe, i).some(x => x.q > 0) : r.q > 0))
    .map(r => {
      if (isSezione(r)) return { descrizione: r.descrizione, sezione: true, importo: 0 };
      const unitario = r2(r.pu * fattore);
      return { descrizione: r.descrizione, quantita: r.q, tariffa: r.tariffa,
               unitario, importo: r2(r.q * unitario) };
    });
  // Arrotondando i prezzi unitari la tabella può scostarsi di qualche
  // centesimo: lo assorbe una voce con quantità 1, dove prezzo e importo
  // coincidono e il conto riga per riga resta esatto.
  const scarto = r2(obiettivo - visibili.reduce((s, r) => s + (r.sezione ? 0 : r.importo), 0));
  const unitaria = visibili.find(r => !r.sezione && r.quantita === 1);
  if (scarto !== 0 && unitaria) {
    unitaria.unitario = r2(unitaria.unitario + scarto);
    unitaria.importo = unitaria.unitario;
  }
  // Il totale di ogni macro voce: la somma delle sue, dopo la ripartizione di
  // fee e maggiorazione, così quadra con le righe stampate sotto.
  visibili.forEach((r, i) => {
    if (r.sezione) r.importo = r2(righeDellaSezione(visibili, i).reduce((s, x) => s + x.importo, 0));
  });
  const totaleVoci = r2(visibili.reduce((s, r) => s + (r.sezione ? 0 : r.importo), 0));
  const scontoVisibile = conSconto ? r2(totaleVoci - prezzo) : 0;

  return {
    righe, subtotale, totaleFee, offerta, sconto, calcolato, prezzo, manuale,
    visibili, scontoVisibile,
    // L'importo dell'offerta è il totale della tabella al cliente: documento e
    // gestionale dicono lo stesso numero, al centesimo.
    importo: subtotale > 0 ? r2(totaleVoci - scontoVisibile) : prezzo,
  };
}

/** Impegno complessivo in giornate: la mezza giornata vale mezza. */
export const giornatePreventivo = (prev) => (prev?.righe || []).reduce((s, r) =>
  s + quantitaRiga(r) * (TIPI_TARIFFA.find(t => t.v === r.tariffa)?.giornate || 0), 0);

/**
 * Giornate e costo per consulente (colonna), con le sue tariffe di profilo.
 * Il lavoro degli interni non ha costo da riconoscere a terzi.
 */
export function perConsulente(prev, consulenti) {
  const CAMPO = { giorno: 'tar_front_giorno', mezza: 'tar_front_mezza', back: 'tar_back_giorno' };
  return (prev.consulenti || []).map((id, col) => {
    if (!id) return null;
    const c = consulenti.find(x => String(x.id) === String(id));
    let giornate = 0, costo = 0, senzaTariffa = false;
    for (const r of prev.righe || []) {
      const q = n(r.quantita?.[col]);
      if (!q) continue;
      giornate += q * (TIPI_TARIFFA.find(t => t.v === r.tariffa)?.giornate || 0);
      if (c?.interno) continue;
      const t = CAMPO[r.tariffa] ? c?.[CAMPO[r.tariffa]] : null;
      if (t === null || t === undefined || t === '') senzaTariffa = true;
      else costo += q * Number(t);
    }
    return { id, col, nome: c ? `${c.nome} ${c.cognome || ''}`.trim() : '?',
             interno: !!c?.interno, giornate, costo: r2(costo), senzaTariffa };
  });
}
